export interface PublicClientConfig {
  /** Public API origin when hosting the client independently, for example on a CDN. */
  backendUrl?: string;
}

declare global {
  interface Window {
    ANDOR_CONFIG?: PublicClientConfig;
  }
}

/**
 * Absolute URLs preserve the existing API services and interceptor contract.
 *
 * `nativeApp` is the active space of the native (Tauri) app, `null` before
 * a space was added there. Its WebView origin (`tauri://localhost`,
 * `http://tauri.localhost`) says nothing about the tenant, so the app talks
 * to that space's domain over HTTPS instead, or to its local server or
 * `ANDOR_CONFIG.backendUrl` (see `nativeBackendUrl`).
 */
export function backendEndpoints(
  page: Pick<Location, 'origin' | 'protocol'>,
  config: PublicClientConfig = window.ANDOR_CONFIG ?? {},
  nativeApp?: NativeSpace | null
) {
  if (nativeApp === null) {
    // No space yet: the app only shows the space picker, nothing talks to
    // a backend.
    return { backendUrl: '', backendWebSocketUrl: '' };
  }
  if (nativeApp !== undefined) {
    const origin = nativeBackendUrl(nativeApp, config);
    return { backendUrl: origin, backendWebSocketUrl: origin.replace(/^http/, 'ws') };
  }
  const url = backendUrl(config.backendUrl?.trim() || page.origin);
  if (page.protocol === 'https:' && url.protocol !== 'https:') {
    throw new Error('ANDOR_CONFIG.backendUrl must use HTTPS when the client uses HTTPS');
  }
  return {
    backendUrl: url.origin,
    backendWebSocketUrl: url.origin.replace(/^http/, 'ws'),
  };
}

function backendUrl(value: string): URL {
  const url = new URL(value);
  if (
    !['http:', 'https:'].includes(url.protocol) ||
    url.username ||
    url.password ||
    url.pathname !== '/' ||
    url.search ||
    url.hash
  ) {
    throw new Error(
      'ANDOR_CONFIG.backendUrl must be an HTTP(S) origin without a path or credentials'
    );
  }
  return url;
}

/** A space of the native app: its tenant domain and, for a local space in
 * a development build, the server it is reached through (see `spaces.ts`). */
export interface NativeSpace {
  domain: string;
  server?: string;
}

/**
 * Backend origin serving the native app's space: the server of a local
 * space, a configured backend (staging, local server) or else the domain
 * itself. The domain always travels in the tenant header, not in this URL.
 */
export function nativeBackendUrl(
  space: NativeSpace,
  config: PublicClientConfig = window.ANDOR_CONFIG ?? {}
): string {
  const native = new URL(`https://${space.domain}`);
  if (native.hostname !== space.domain.toLowerCase() || native.port) {
    throw new Error('A space must be a plain domain name');
  }
  const override = space.server ?? config.backendUrl?.trim();
  return override ? nativeBackendOrigin(override) : native.origin;
}

/** Native backend override: HTTPS, or plain HTTP only for a local server. */
function nativeBackendOrigin(value: string): string {
  const url = backendUrl(value);
  const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  if (url.protocol !== 'https:' && !loopback) {
    throw new Error('The app must use HTTPS unless the backend is a local server');
  }
  return url.origin;
}
